import { useState, useEffect, useRef, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { formatNumber } from '../../../utils/formatNumber';
import { pricingService } from '../../../services/pricingService';
import { supabase } from '../../../lib/supabase';

const PricingPanel = ({ formData }) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);
  const calculationTimeoutRef = useRef(null);
  const lastFormDataRef = useRef(null);

  // Memoize equipment array to prevent unnecessary recalculations
  const equipmentKey = useMemo(() => {
    if (!formData?.equipment || !Array.isArray(formData.equipment)) return '';
    return formData.equipment.sort().join(',');
  }, [formData?.equipment]);

  // Create a stable key for form data to detect changes
  const formDataKey = useMemo(() => {
    return JSON.stringify({
      photographyType: formData?.photographyType,
      city: formData?.city,
      duration: formData?.duration,
      experienceLevel: formData?.experienceLevel,
      equipment: equipmentKey,
      workloadBucket: formData?.workloadBucket,
      productPhysicalSizeBucket: formData?.productPhysicalSizeBucket,
      shootingMethod: formData?.shootingMethod,
      shootingMethods: formData?.shootingMethods,
      shootingPlaceType: formData?.shootingPlaceType,
      deliverableMode: formData?.deliverableMode,
      photosCount: formData?.photosCount,
      videosCount: formData?.videosCount,
      shortVideosCount: formData?.shortVideosCount
    });
  }, [
    formData?.photographyType,
    formData?.city,
    formData?.duration,
    formData?.experienceLevel,
    equipmentKey,
    formData?.workloadBucket,
    formData?.productPhysicalSizeBucket,
    formData?.shootingMethod,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formData?.shootingMethods),
    formData?.shootingPlaceType,
    formData?.deliverableMode,
    formData?.photosCount,
    formData?.videosCount,
    formData?.shortVideosCount
  ]);

  // On mount: load config immediately for partial estimate display
  useEffect(() => {
    pricingService.getPricingConfig(true).then(setPricingConfig);
  }, []);

  // Called whenever admin changes pricing — clears cache + forces fresh re-fetch + re-calc
  const triggerPricingRefresh = useRef(null);
  triggerPricingRefresh.current = async () => {
    pricingService.clearPricingCache();
    const fresh = await pricingService.getPricingConfig(true);
    setPricingConfig(fresh);
    lastFormDataRef.current = null; // force recalculation
    console.log('🔄 PricingPanel: pricing refreshed from DB');
  };

  // Realtime sync — unique channel name avoids Supabase SDK conflicts
  useEffect(() => {
    const channelName = `pricing-panel-${Date.now()}`;

    // BroadcastChannel: same-browser cross-tab (most reliable for same device)
    let bc = null;
    try {
      bc = new BroadcastChannel('pricing-cache-clear');
      bc.onmessage = () => triggerPricingRefresh.current?.();
    } catch (_) { /* unsupported */ }

    // postgres_changes: cross-device / cross-browser updates
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pricing_workload_multipliers' }, () => triggerPricingRefresh.current?.())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pricing_photography_type_multipliers' }, () => triggerPricingRefresh.current?.())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'pricing_settings' }, () => triggerPricingRefresh.current?.())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pricing_hours_multipliers' }, () => triggerPricingRefresh.current?.())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pricing_place_type_multipliers' }, () => triggerPricingRefresh.current?.())
      .subscribe();

    return () => {
      try { bc?.close(); } catch (_) { /* ignore */ }
      supabase.removeChannel(channel);
    };
  }, []);

  // Price calculation — async so it always fetches the latest config from service
  useEffect(() => {
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    // Only calculate if we have minimum required data (city, photographyType, duration)
    if (!formData?.photographyType || !formData?.city || !formData?.duration) {
      setPricing(null);
      lastFormDataRef.current = null;
      return;
    }

    // Skip if nothing changed
    if (lastFormDataRef.current === formDataKey) {
      return;
    }

    calculationTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      // Mark immediately to prevent duplicate runs while awaiting
      lastFormDataRef.current = formDataKey;

      try {
        // Always fetch latest config from service (uses module-level cache; auto-refreshes when admin clears it)
        const config = await pricingService.getPricingConfig();
        // Keep pricingConfig state in sync so estimatedPrice useMemo stays fresh
        setPricingConfig(prev => (prev === config ? prev : config));

        const priceBreakdown = pricingService.calculateFinalPrice(formData, config);

        console.log('💰 PricingPanel: Price calculated', {
          total: priceBreakdown.total,
          workloadBucket: priceBreakdown.workloadBucket,
          workloadMultiplier: priceBreakdown.workloadMultiplier,
        });

        setPricing({
          basePrice: priceBreakdown.baseHourPrice,
          vat: priceBreakdown.vatAmount,
          total: priceBreakdown.total,
          subtotal: priceBreakdown.subtotal,
          breakdown: priceBreakdown.breakdown,
          hours: priceBreakdown.hours,
          multipliers: priceBreakdown.multipliers,
          deliverables: priceBreakdown.deliverables
        });
      } catch (err) {
        console.error('Error calculating price:', err);
        setError('فشل حساب السعر. يرجى المحاولة مرة أخرى.');
        setPricing(null);
        lastFormDataRef.current = null;
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
  }, [formDataKey, pricingConfig]);

  // Check what data we have for partial pricing display
  const hasMinimumData = formData?.photographyType && formData?.city && formData?.duration;
  const hasPartialData = formData?.city || formData?.photographyType || formData?.duration;
  
  // Calculate estimated price — uses exact same engine as full price for accuracy.
  // If duration isn't selected yet, defaults to 2 hours so all other multipliers
  // (city, type, workload, hours-from-DB, etc.) are applied correctly from the start.
  const estimatedPrice = useMemo(() => {
    if (!pricingConfig || !hasPartialData) return null;

    const draftWithDefaults = {
      ...formData,
      duration: formData?.duration || '2 ساعة',
    };

    try {
      const result = pricingService.calculateFinalPrice(draftWithDefaults, pricingConfig);
      const durationSelected = !!formData?.duration;
      const appliedMultipliers = result.breakdown
        .filter(item =>
          item.type !== 'base' &&
          // Hide hours multiplier until the user actually picks a duration
          !(item.type === 'hours' && !durationSelected) &&
          !item.isAddition &&
          item.multiplier != null &&
          item.multiplier !== 1
        )
        .map(item => ({ label: item.label, value: item.multiplier }));

      return {
        // Show per-hour rate (consistent with full pricing display)
        basePrice: result.baseHourPrice,
        subtotal: result.subtotal,
        vat: result.vatAmount,
        total: result.total,
        hours: result.hours,
        appliedMultipliers,
        deliverablesCost: result.deliverables?.totalCost || 0,
        photosCost: result.deliverables?.photosCost || 0,
        videosCost: result.deliverables?.videosCost || 0,
        shortVideosCost: result.deliverables?.shortVideosCost || 0,
        photosDiscount: result.deliverables?.photosDiscount || 0,
        videosDiscount: result.deliverables?.videosDiscount || 0,
        shortVideosDiscount: result.deliverables?.shortVideosDiscount || 0,
        photosCount: result.deliverables?.photosCount || 0,
        videosCount: result.deliverables?.videosCount || 0,
        shortVideosCount: result.deliverables?.shortVideosCount || 0,
        isEstimate: !hasMinimumData,
      };
    } catch {
      return null;
    }
  }, [formData, pricingConfig, hasPartialData, hasMinimumData]);

  return (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-6 shadow-lg border-2 border-primary/20 sticky top-24">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Calculator" size={24} color="var(--color-primary)" />
        <h3 className="text-xl font-bold text-foreground">
          التسعير الذكي
        </h3>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
          <p className="text-sm text-muted-foreground">جاري حساب السعر...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-3 opacity-50" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : estimatedPrice && !hasMinimumData ? (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                سعر تقديري - سيتم تحديثه عند إكمال جميع البيانات المطلوبة
              </p>
            </div>
          </div>
          
          <div className="bg-background/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {formData?.duration
                  ? `السعر الأساسي (${estimatedPrice.hours} ساعة)`
                  : 'سعر الساعة الأساسي'}
              </span>
              <span className="font-medium text-foreground">{formatNumber(estimatedPrice.basePrice)} ر.س</span>
            </div>

            {/* المضاعفات مخفية من واجهة العميل */}

            {/* Deliverables in estimate */}
            {estimatedPrice.deliverablesCost > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">تسعير المخرجات:</p>
                {estimatedPrice.photosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Image" size={12} className="text-blue-500" />
                      {estimatedPrice.photosCount} صورة
                      {estimatedPrice.photosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{estimatedPrice.photosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(estimatedPrice.photosCost)} ر.س</span>
                  </div>
                )}
                {estimatedPrice.videosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Video" size={12} className="text-blue-500" />
                      {estimatedPrice.videosCount} فيديو طويل
                      {estimatedPrice.videosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{estimatedPrice.videosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(estimatedPrice.videosCost)} ر.س</span>
                  </div>
                )}
                {estimatedPrice.shortVideosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Clapperboard" size={12} className="text-purple-500" />
                      {estimatedPrice.shortVideosCount} فيديو قصير
                      {estimatedPrice.shortVideosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{estimatedPrice.shortVideosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(estimatedPrice.shortVideosCost)} ر.س</span>
                  </div>
                )}
              </div>
            )}

          </div>

          <div className="bg-primary/10 rounded-xl p-4 border-2 border-primary border-dashed">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">السعر التقديري</span>
              <span className="text-2xl font-bold text-primary">{formatNumber(estimatedPrice.total)} ر.س</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">السعر النهائي شامل جميع الرسوم</p>
          </div>

          <div className="bg-background/50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">البيانات المطلوبة:</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.city ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.city ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.city ? "text-foreground" : "text-muted-foreground"}>
                  المدينة {formData?.city ? '✓' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.photographyType ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.photographyType ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.photographyType ? "text-foreground" : "text-muted-foreground"}>
                  نوع التصوير {formData?.photographyType ? '✓' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.duration ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.duration ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.duration ? "text-foreground" : "text-muted-foreground"}>
                  المدة {formData?.duration ? '✓' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : !hasPartialData ? (
        <div className="space-y-3">
          <div className="text-center py-4">
            <Icon name="Info" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-50" />
            <p className="text-sm text-muted-foreground mb-2">
              ابدأ بالإجابة على الأسئلة لحساب السعر الدقيق
            </p>
          </div>
          <div className="bg-background/50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">البيانات المطلوبة:</p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.city ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.city ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.city ? "text-foreground" : "text-muted-foreground"}>
                  المدينة
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.photographyType ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.photographyType ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.photographyType ? "text-foreground" : "text-muted-foreground"}>
                  نوع التصوير
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={formData?.duration ? "CheckCircle2" : "Circle"} 
                  size={14} 
                  color={formData?.duration ? "var(--color-success)" : "var(--color-muted-foreground)"} 
                />
                <span className={formData?.duration ? "text-foreground" : "text-muted-foreground"}>
                  المدة
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : pricing ? (
        <div className="space-y-4">
          <div className="bg-background/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">السعر الأساسي ({pricing.hours} ساعة)</span>
              <span className="font-medium text-foreground">{formatNumber(pricing.basePrice)} ر.س</span>
            </div>

            {/* المضاعفات مخفية من واجهة العميل */}

            {/* Show deliverables cost */}
            {pricing.deliverables && pricing.deliverables.mode === 'custom' && pricing.deliverables.totalCost > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">تسعير المخرجات:</p>
                {pricing.deliverables.photosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Image" size={12} className="text-blue-500" />
                      {pricing.deliverables.photosCount} صورة
                      {pricing.deliverables.photosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{pricing.deliverables.photosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(pricing.deliverables.photosCost)} ر.س</span>
                  </div>
                )}
                {pricing.deliverables.videosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Video" size={12} className="text-blue-500" />
                      {pricing.deliverables.videosCount} فيديو طويل
                      {pricing.deliverables.videosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{pricing.deliverables.videosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(pricing.deliverables.videosCost)} ر.س</span>
                  </div>
                )}
                {pricing.deliverables.shortVideosCost > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Icon name="Clapperboard" size={12} className="text-purple-500" />
                      {pricing.deliverables.shortVideosCount} فيديو قصير
                      {pricing.deliverables.shortVideosDiscount > 0 && (
                        <span className="text-green-600 text-[10px]">(-{pricing.deliverables.shortVideosDiscount}%)</span>
                      )}
                    </span>
                    <span className="font-medium text-green-600">+{formatNumber(pricing.deliverables.shortVideosCost)} ر.س</span>
                  </div>
                )}
              </div>
            )}

          </div>

          <div className="bg-primary/10 rounded-xl p-4 border-2 border-primary">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">الإجمالي</span>
              <span className="text-2xl font-bold text-primary">{formatNumber(pricing.total)} ر.س</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">السعر النهائي شامل جميع الرسوم</p>
          </div>

          <div className="bg-background/50 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                السعر النهائي محسوب ديناميكياً بناءً على اختياراتك وقواعد التسعير الذكية.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PricingPanel;